import { adsApi } from '@/api/client';
import type { KeywordEntityRow } from '@/api/unifiedKeywords';
import { parseKeywordsPayload } from '@/api/keywordMetrics';
import { parseAdGroupsListPayload } from '@/utils/unifiedAdGroups';

type SpKeywordListBody = {
  include_extended_data_fields?: boolean;
  states?: Array<'ENABLED' | 'PAUSED' | 'ARCHIVED'>;
  max_results?: number;
};

type SpAdGroupListBody = {
  include_extended_data_fields?: boolean;
  states?: Array<'ENABLED' | 'PAUSED' | 'ARCHIVED'>;
  max_results?: number;
};

export type FetchAllResult<T> = {
  items: T[];
  hasMore: boolean;
};

export async function fetchAllSpKeywords(
  profileId: string,
  body: SpKeywordListBody,
  options?: { fetchAll?: boolean }
): Promise<FetchAllResult<KeywordEntityRow>> {
  const fetchAll = options?.fetchAll ?? false;
  const items: KeywordEntityRow[] = [];
  let nextToken: string | null = null;
  let hasMore = false;

  do {
    const res = await adsApi.listSpKeywords(profileId, {
      ...body,
      next_token: nextToken,
    });
    if (!res.success) throw new Error('Failed to load SP keywords');
    const parsed = parseKeywordsPayload(res.data);
    items.push(...parsed.keywords);
    nextToken = parsed.nextToken;
    hasMore = Boolean(nextToken);
    if (!fetchAll) break;
  } while (nextToken);

  return { items, hasMore };
}

export async function fetchAllSpAdGroups(
  profileId: string,
  body: SpAdGroupListBody,
  options?: { fetchAll?: boolean }
): Promise<FetchAllResult<Record<string, unknown>>> {
  const fetchAll = options?.fetchAll ?? false;
  const items: Record<string, unknown>[] = [];
  let nextToken: string | null = null;
  let hasMore = false;

  do {
    const res = await adsApi.listSpAdGroups(profileId, {
      ...body,
      next_token: nextToken,
    });
    if (!res.success) throw new Error('Failed to load SP ad groups');
    const parsed = parseAdGroupsListPayload(res.data);
    items.push(...(parsed.adGroups as Record<string, unknown>[]));
    nextToken = parsed.nextToken;
    hasMore = Boolean(nextToken);
    if (!fetchAll) break;
  } while (nextToken);

  return { items, hasMore };
}
